import { useEffect, useState } from 'react';
import { useContactRequests, type ContactRequest } from './useContacts';
import { useChats } from './useChats';

interface NotificationData {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
}

export function useNotifications() {
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const { data: contactRequests } = useContactRequests();
  const { data: chats } = useChats();

  // Request permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  // Show notifications for new contact requests
  useEffect(() => {
    if (!contactRequests || permissionStatus !== 'granted') return;

    const pendingRequests = contactRequests.filter(
      r => r.direction === 'received' && r.status === 'pending'
    );

    // Only notify for truly new requests (you could track seen IDs)
    pendingRequests.forEach(request => {
      if (document.hidden || !document.hasFocus()) {
        showNotification({
          title: 'Nueva solicitud de contacto',
          body: `${request.fromNickname} quiere agregarte como contacto`,
          icon: '/mensag-logo.png',
          badge: '/badge.png',
          tag: `contact-request-${request.id}`,
          data: { type: 'contact_request', requestId: request.id }
        });
      }
    });
  }, [contactRequests, permissionStatus]);

  // Show notifications for new messages in chats
  useEffect(() => {
    if (!chats || permissionStatus !== 'granted') return;

    chats.forEach(chat => {
      if (chat.unreadCount > 0 && (document.hidden || !document.hasFocus())) {
        showNotification({
          title: 'Nuevo mensaje',
          body: `${chat.contactName}: ${chat.lastMessage}`,
          icon: chat.contactAvatar || '/avatar.png',
          tag: `chat-${chat.id}`,
          data: { type: 'chat_message', chatId: chat.contactId }
        });
      }
    });
  }, [chats, permissionStatus]);

  const requestPermission = async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  };

  const showNotification = (data: NotificationData) => {
    if (permissionStatus !== 'granted') return;

    const notification = new Notification(data.title, {
      body: data.body,
      icon: data.icon || '/mensag-logo.png',
      badge: data.badge || '/badge.png',
      tag: data.tag,
      data: data.data,
      silent: false,
      requireInteraction: false,
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    // Handle click
    notification.onclick = () => {
      window.focus();

      // Navigate based on notification type
      if (data.data?.type === 'contact_request') {
        // Could navigate to contact requests screen
        window.location.hash = '#/'; // Navigate to main screen
      } else if (data.data?.type === 'chat_message') {
        window.location.hash = `#/chat/${data.data.chatId}`;
      }

      notification.close();
    };

    return notification;
  };

  const showTestNotification = () => {
    showNotification({
      title: '¡Hola desde MensaG!',
      body: 'Esta es una notificación de prueba. Las notificaciones funcionan correctamente.',
      icon: '/mensag-logo.png',
      tag: 'test-notification'
    });
  };

  return {
    permissionStatus,
    requestPermission,
    showNotification,
    showTestNotification,
    isSupported: 'Notification' in window
  };
}